/**
 * DailyRepsScreen (Screen 4)
 * 
 * "Play with words that actually show up on GRE & GMAT."
 * Ultra-compact, balanced layout with side-by-side heading and grid.
 */
import React from 'react';
import { useOnboardingViewport } from '../shared/useViewport';

const LegendItem = ({ color, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
    <div style={{ 
      width: '12px', 
      height: '12px', 
      borderRadius: '4px', 
      background: color, 
      flexShrink: 0,
      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
    }} />
    <div style={{ textAlign: 'left' }}>
      <div style={{ fontSize: '13.5px', fontWeight: '500', color: '#334155', lineHeight: '1.2', letterSpacing: '-0.2px' }}>{title}</div>
    </div>
  </div>
);

const JourneyStep = ({ icon, title, subtitle, active, last, vp }) => (
  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
    <div style={{
      width: `${vp.sp(44, 48, 52)}px`,
      height: `${vp.sp(44, 48, 52)}px`,
      borderRadius: '50%',
      background: 'white',
      border: `2px solid ${active ? '#FCA5A5' : '#E2E8F0'}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: active ? '0 6px 15px rgba(248, 113, 113, 0.12)' : '0 4px 12px rgba(0,0,0,0.03)',
      zIndex: 2,
      position: 'relative'
    }}>
      {React.cloneElement(icon, { width: vp.sp(20, 22, 24), height: vp.sp(20, 22, 24) })}
    </div>
    <div style={{ marginTop: '8px', textAlign: 'center', minHeight: '34px' }}>
      <div style={{ fontSize: vp.sp(10.5, 11.5, 12.5), fontWeight: '900', color: '#0F172A', letterSpacing: '-0.2px' }}>{title}</div>
      <div style={{ 
        fontSize: '9px', 
        color: '#64748B', 
        lineHeight: '1.2', 
        marginTop: '2px', 
        maxWidth: '75px', 
        margin: '2px auto 0',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.3px',
        opacity: 0.7
      }}>
        {subtitle}
      </div>
    </div>
    {!last && (
      <div style={{
        position: 'absolute',
        top: `${vp.sp(22, 24, 26)}px`,
        left: 'calc(50% + 22px)',
        width: 'calc(100% - 44px)',
        height: '1.5px',
        background: '#E2E8F0',
        zIndex: 1
      }} />
    )}
  </div>
);

export const DailyRepsScreen = ({ onNext, user, handleGoogleLogin }) => {
  const vp = useOnboardingViewport();

  const primaryCTAStyle = {
    width: "100%",
    height: `${vp.spacing.ctaHeight}px`,
    background: "#14B8A6",
    borderRadius: `${vp.spacing.ctaBorderRadius}px`,
    border: "none",
    boxShadow: "0 8px 16px rgba(20, 184, 166, 0.2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    color: "white",
    fontSize: `${vp.spacing.ctaFontSize}px`,
    fontWeight: "900",
    cursor: "pointer",
    transition: "transform 0.2s",
  };

  const isDesktop = vp.isTablet;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      width: '100%',
      maxWidth: isDesktop ? '480px' : '100%',
      margin: isDesktop ? '0 auto' : undefined,
      animation: 'fadeIn 0.6s ease-out',
      position: 'relative',
      background: 'transparent'
    }}>
      {/* 1. Top Section: Headline & Mascot — Increased bottom margin to push content down */}
      <div style={{ 
        width: '100%', 
        minHeight: `${vp.sp(140, 160, 180)}px`, 
        position: 'relative', 
        marginTop: '-34px',
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: `${vp.sp(12, 18, 24)}px`
      }}>
        {/* Left: Text Content */}
        <div style={{ flex: 1, textAlign: 'left', paddingTop: '15px', paddingLeft: '16px', zIndex: 1, position: 'relative' }}>
          <h2 style={{
            fontSize: vp.sp(26, 30, 34),
            fontWeight: '900',
            color: '#0F172A',
            lineHeight: '1.05',
            margin: '0 0 6px 0',
            letterSpacing: '-1.5px'
          }}>
            Play with words<br />
            that actually<br />
            show up on<br />
            <span style={{ color: '#14B8A6' }}>GRE & GMAT.</span>
          </h2>
          <p style={{ 
            fontSize: vp.sp(11.5, 12, 12.5),
            color: '#64748B', 
            lineHeight: '1.3', 
            fontWeight: '600', 
            maxWidth: '210px',
            opacity: 0.9
          }}>
            Every game uses high-frequency exam-relevant words.
          </p>
        </div>
        
        {/* Right/Overlapping: Mascot */}
        <div style={{
          width: `${vp.sp(140, 160, 180)}px`,
          height: '100%',
          position: 'absolute',
          top: '0',
          right: '12px',
          zIndex: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
           <div style={{ position: 'absolute', top: '10%', right: '10%', color: '#8B5CF6', fontSize: '20px', opacity: 0.3 }}>✦</div>
           <img 
             src="/images/onboarding/page4.webp"
             alt="Brain mascot reading"
             style={{
               width: '100%',
               height: 'auto',
               maxHeight: `${vp.sp(145, 170, 195)}px`,
               objectFit: 'contain',
               display: 'block',
               filter: 'drop-shadow(0 15px 30px rgba(0,0,0,0.08))',
               marginTop: `${vp.sp(0, 10, 20)}px`
             }}
           />
        </div>
      </div>

      {/* 2. Your Word Map Card — Grid beside Heading */}
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: `${vp.sp(12, 16, 20)}px 20px`,
        marginTop: `${vp.sp(4, 8, 12)}px`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
        border: '1px solid #F1F5F9',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 3,
        position: 'relative'
      }}>
        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          {/* Left Column: Heading + Legend */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '14px', minWidth: '130px' }}>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '15.5px', fontWeight: '900', color: '#0F172A', letterSpacing: '-0.3px' }}>Your Word Map</div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <LegendItem color="#FF6B6B" title="Weak Words" />
              <LegendItem color="#FFD93D" title="Learning" />
              <LegendItem color="#4ADE80" title="Strong Words" />
              <LegendItem color="#F1F5F9" title="New Words" />
            </div>
          </div>

          {/* Right Column: Grid — Now starts beside the heading */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(6, 1fr)',
            gap: '6px',
            width: `${vp.sp(140, 160, 180)}px`,
            paddingTop: '4px' // Minor alignment adjustment
          }}>
            {[
              '#F1F5F9', '#F1F5F9', '#F1F5F9', '#F1F5F9', '#F1F5F9', '#F1F5F9',
              '#F1F5F9', '#F1F5F9', '#F1F5F9', '#FFD93D', '#F1F5F9', '#F1F5F9',
              '#F1F5F9', '#FFD93D', '#FFD93D', '#F1F5F9', '#FFD93D', '#F1F5F9',
              '#FF6B6B', '#F1F5F9', '#F1F5F9', '#4ADE80', '#4ADE80', '#4ADE80',
              '#FF6B6B', '#FF6B6B', '#F1F5F9', '#4ADE80', '#4ADE80', '#4ADE80'
            ].map((color, i) => (
              <div key={i} style={{
                width: '100%',
                aspectRatio: '1',
                background: color,
                borderRadius: '4px',
                boxShadow: color === '#F1F5F9' ? 'none' : 'inset 0 0 0 1px rgba(0,0,0,0.05)'
              }} />
            ))}
          </div>
        </div>
      </div>

      {/* 3. Your Vocabulary Journey Card — Increased top margin */}
      <div style={{
        background: 'white',
        borderRadius: '24px',
        padding: `${vp.sp(14, 18, 22)}px 20px`,
        marginTop: `${vp.sp(18, 24, 30)}px`,
        boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
        border: '1px solid #F1F5F9',
        textAlign: 'center',
        zIndex: 3,
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '14px' }}>
           <div style={{ color: '#E2E8F0', fontSize: '12px' }}>✦</div>
           <div style={{ fontSize: '13px', fontWeight: '800', color: '#0F172A', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Your Vocabulary Journey</div>
           <div style={{ color: '#E2E8F0', fontSize: '12px' }}>✦</div>
        </div>

        <div style={{ display: 'flex', width: '100%' }}>
          <JourneyStep 
            vp={vp}
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>}
            title="Start"
            subtitle="At your level"
            active
          />
          <JourneyStep 
            vp={vp}
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="2.5"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>}
            title="Learn"
            subtitle="Curated words"
          />
          <JourneyStep 
            vp={vp}
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#8B5CF6" strokeWidth="2.5"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h.01M18 12h.01M15 12h.01M9 12h.01M12 9v6"/></svg>}
            title="Play"
            subtitle="Adaptive games"
          />
          <JourneyStep 
            vp={vp}
            icon={<svg viewBox="0 0 24 24" fill="none" stroke="#4ADE80" strokeWidth="2.5"><path d="M3 17l6-6 4 4 8-8"/><polyline points="14 7 21 7 21 14"/></svg>}
            title="Improve"
            subtitle="Weak words"
            last
          />
        </div>
      </div>

      {/* 4. CTA Button — Shifted down further */}
      <div style={{
        paddingTop: '40px', // More breathing room from content above
        zIndex: 10
      }}>
      {!user?.id ? (
        <button
          onClick={async () => {
            try {
              sessionStorage.setItem("postLoginRedirect", "page4");
              await handleGoogleLogin();
            } catch (e) {
              console.error("Login failed", e);
            }
          }}
          style={{
            width: "100%",
            height: `${vp.sp(48, 54, 60)}px`,
            background: "white",
            borderRadius: "16px",
            border: "1px solid #F1F5F9",
            boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
            cursor: "pointer"
          }}
        >
          <svg width="20" height="20" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
          </svg>
          <span style={{ fontSize: "15.5px", fontWeight: "700", color: "#0F172A" }}>Continue with Google</span>
        </button>
      ) : (
        <button
          onClick={onNext}
          style={primaryCTAStyle}
        >
          Start Your Word Journey
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7" />
          </svg>
        </button>
      )}
      </div>
    </div>
  );
};

export default DailyRepsScreen;
